/**
 * HTTP-client error-code vocabulary: the `code:` discriminator on `AdminAPIError` and the `httpFetch` result union.
 *  - REQUEST_CANCELLED: the CALLER abandoned the request -- distinct from TIMEOUT, the client chose to stop.
 *  - TIMEOUT: the window expired before any response arrived. NETWORK_ERROR: a `TypeError` mentioning `fetch` -- DNS, TCP reset, CORS preflight.
 *  - API_ERROR: non-2xx with no explicit `code` in the body.
 *  - UNKNOWN_ERROR: an unrecognised throw shape -- a degraded last-resort bucket, not a normal code.
 */

import type { SelfKeyed } from '@federa/types/primitives-records';

const HTTP_CLIENT_ERROR_CODES_SOURCE = {
	REQUEST_CANCELLED: 'REQUEST_CANCELLED',
	TIMEOUT: 'TIMEOUT',
	NETWORK_ERROR: 'NETWORK_ERROR',
	API_ERROR: 'API_ERROR',
	UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

export const HTTP_CLIENT_ERROR_CODES: SelfKeyed<typeof HTTP_CLIENT_ERROR_CODES_SOURCE> = HTTP_CLIENT_ERROR_CODES_SOURCE;

/** Tightens the producer/consumer fields off bare `string`; see the adjudication above. */
export type HttpClientErrorCode = (typeof HTTP_CLIENT_ERROR_CODES)[keyof typeof HTTP_CLIENT_ERROR_CODES];

/** A DISTINCT surface from `HTTP_CLIENT_ERROR_CODES.TIMEOUT`: that is the wire-envelope discriminator, this is the producer-side `Error.name` retry consumers branch on to silence the unawaited fetch rejection after a timeout already threw. */
export const HTTP_TIMEOUT_ERROR_NAME = 'TimeoutError' as const;

/**
 * The DOM `Error.name` an aborted operation throws with -- what `AbortController.abort()` and a bare `AbortSignal`
 * cancellation surface, and the name every cancellation-vs-failure branch in the tree tests for. A SIBLING of
 * {@link HTTP_TIMEOUT_ERROR_NAME}, not a synonym: `AbortSignal.timeout()` raises `TimeoutError` while a caller-driven
 * `abort()` raises this one, which is why the health probes read an abort as "the port answered nothing in budget"
 * while the retry classifier reads it as transient.
 *
 * Homed here rather than at any one consumer because the consumers span layers that cannot import each other --
 * `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` bars a `utils/` leaf from a `utils/` sibling -- so a const leaf is the only
 * shared home, for the name and for {@link isAbortError} alike.
 */
export const ABORT_ERROR_NAME = 'AbortError' as const;

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): true for an AbortController-originated abort (a cancellation, not a
 * failure). Const-homed so a `utils/` probe reaches it rather than spelling the same test itself.
 */
export function isAbortError(error: unknown): boolean {
	return error instanceof Error && error.name === ABORT_ERROR_NAME;
}
